/**
 * deliveryStatus — the one rung a bubble shows, derived from ledger facts
 * (2026-09-08, PR-2). Pure.
 *
 *   ○  pending            no fact yet (the send is seconds old)
 *   ⧗  queued-offline     browser offline at send; waiting for connection
 *   ✓  sent               the sender's own copy is on the server
 *   ✓✓ delivered          the thread carries it (group echo, or the turn was
 *                         accepted in a solo thread)
 *   ✓✓ read (accent)      a participant's receipt passed it (PR-3)
 *   ✓! attachments-missing the message landed, a file did not — retry
 *   !  failed             not sent — retry
 *
 * `null` = render nothing: not a user message, no shared id, or a message
 * this browser never saw sent (history). Precedence: failed >
 * attachments-missing > read > delivered > sent > queued-offline > pending.
 * A solo thread's `accepted` is delivery: with no other participant, the
 * server having the turn is the whole story.
 *
 * WHY attachments-missing OUTRANKS read AND delivered (2026-09-11).
 *
 * Malaika sent screenshots from an iPhone and nobody saw them, herself
 * included. Her message showed ✓✓. It was not lying about the text — the
 * words did reach the thread — but the ✓✓ was answering a question nobody
 * asked. `deriveDeliveryStatus` took `{isGroup, readByOther}` and no
 * attachment input at all, so "delivered" meant "a document exists", never
 * "what you sent is what they got".
 *
 * A message whose photo was dropped is not delivered in the sense the sender
 * means, and telling them it was READ makes it worse: they believe the
 * picture was looked at. So the incomplete fact wins over both, and the copy
 * says which half arrived.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';
import type { DeliveryFacts } from './deliveryLedger';

export type DeliveryStatus =
  | 'pending'
  | 'queued-offline'
  | 'queued-behind-turn'
  | 'sent'
  | 'delivered'
  | 'noted'
  | 'read'
  | 'attachments-missing'
  | 'failed';

export interface DeliveryStatusContext {
  /** True when the thread has other human participants (the shared store carries it). */
  isGroup: boolean;
  /**
   * PR-3: another participant's receipt covers this message (seenBy.readersOf
   * is non-empty). Lifts a delivered message to 'read'; like every rung it
   * needs session facts — a reload still paints nothing on history (the
   * SeenByMarks carries the durable read state).
   */
  readByOther?: boolean;
}

export function deriveDeliveryStatus(
  message: Pick<ChatMessage, 'isUser' | 'sharedMessageId'> | null | undefined,
  facts: DeliveryFacts | undefined,
  ctx: DeliveryStatusContext,
): DeliveryStatus | null {
  if (!message || message.isUser !== true) return null;
  if (typeof message.sharedMessageId !== 'string' || !message.sharedMessageId.trim()) return null;
  if (!facts) return null;
  if (facts.failedAt !== undefined) return 'failed';
  if (facts.attachmentsMissingAt !== undefined) return 'attachments-missing';
  if (facts.readAt !== undefined || ctx.readByOther === true) return 'read';
  // A mid-turn note the server ACCEPTED onto the running task (2026-09-15):
  // the server has it — delivery — and it says more than ✓✓ does: it is being
  // folded into the work in progress. Outranks plain delivery for that reason.
  if (facts.notedAt !== undefined) return 'noted';
  if (facts.deliveredAt !== undefined) return 'delivered';
  if (!ctx.isGroup && facts.acceptedAt !== undefined) return 'delivered';
  if (facts.ownAckedAt !== undefined) return 'sent';
  if (facts.queuedBehindTurnAt !== undefined) return 'queued-behind-turn';
  if (facts.queuedOfflineAt !== undefined) return 'queued-offline';
  return 'pending';
}

/** Chrome copy (D9: status, never content). */
// copy: delivery_receipt
export const DELIVERY_STATUS_COPY: Record<DeliveryStatus, { glyph: string; label: string; es: string }> = {
  pending: { glyph: '○', label: 'sending…', es: 'enviando…' },
  'queued-offline': { glyph: '⧗', label: "you're offline — this will send when you're back", es: 'sin conexión — se enviará cuando vuelvas' },
  // The one queued send left (2026-09-15): the in-flight turn could not take a
  // note, so this sends the moment it finishes. State words on the sender's
  // bubble — never a "Queued —" line under Prest0n's name.
  'queued-behind-turn': { glyph: '⧗', label: 'sends when the current reply finishes', es: 'se enviará cuando termine la respuesta actual' },
  sent: { glyph: '✓', label: 'sent', es: 'enviado' },
  delivered: { glyph: '✓✓', label: 'delivered', es: 'entregado' },
  // Attached to the running task; folds into the work in progress.
  noted: { glyph: '✓✓', label: 'seen · folding into the reply in progress', es: 'visto · se incorpora a la respuesta en curso' },
  read: { glyph: '✓✓', label: 'seen', es: 'visto' },
  // The text arrived and a file did not — so the glyph keeps the ✓ that is
  // TRUE and adds the mark that is not. Never '!' alone: that would read as
  // "nothing sent", which is its own untruth.
  'attachments-missing': { glyph: '✓!', label: "sent, but the file didn't · retry", es: 'enviado, pero el archivo no · reintentar' },
  failed: { glyph: '!', label: 'not sent · retry', es: 'no enviado · reintentar' },
};

/**
 * The reader's own human turns present in a shared-store snapshot — the
 * "delivered" fact for each. Pure so the thread store's listener stays a
 * one-liner and the rule is unit-tested.
 *
 * DELIVERED MEANS COMPLETE (2026-09-11). A turn whose echo carries FEWER
 * attachment records than the sender attached is not delivery: the server
 * sanitizer drops any record with no durable locator, and no heal path ever
 * re-posts it, so what the others have is the text and nothing else. Those
 * ids come back separately, as `incomplete`, so the caller can say so rather
 * than paint a ✓✓ over a photo that never arrived.
 *
 * `expectedAttachmentCounts` is what the SENDER's own copy carried. Absent —
 * an older caller, or a thread with no attachments in play — every own turn
 * counts as delivered exactly as before.
 */
export interface OwnDeliveredIds {
  /** Echoed in full: the text and every attachment the sender sent. */
  delivered: string[];
  /** Echoed, but short of attachments — with how many are missing. */
  incomplete: Array<{ id: string; missing: number }>;
}

export function collectOwnDeliveredIds(
  overlay: ReadonlyArray<Pick<ChatMessage, 'id' | 'isUser' | 'senderId' | 'attachmentsSerialized'>> | null | undefined,
  uid: string | null | undefined,
  expectedAttachmentCounts?: ReadonlyMap<string, number> | null,
): OwnDeliveredIds {
  const me = (uid ?? '').trim();
  if (!me || !Array.isArray(overlay)) return { delivered: [], incomplete: [] };
  const delivered: string[] = [];
  const incomplete: Array<{ id: string; missing: number }> = [];
  for (const m of overlay) {
    if (!m || m.isUser !== true) continue;
    if ((typeof m.senderId === 'string' ? m.senderId.trim() : '') !== me) continue;
    const id = typeof m.id === 'string' ? m.id.trim() : '';
    if (!id) continue;
    const expected = expectedAttachmentCounts?.get(id);
    if (typeof expected === 'number' && expected > 0) {
      const got = Array.isArray(m.attachmentsSerialized) ? m.attachmentsSerialized.length : 0;
      if (got < expected) {
        incomplete.push({ id, missing: expected - got });
        continue;
      }
    }
    delivered.push(id);
  }
  return { delivered, incomplete };
}
